import queryKeys from 'config/queryKeys';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Handle, NodeProps, Position } from 'reactflow';
import * as remoteApi from 'api/remote';
import styled from 'styled-components';
import theme from 'theme';
import { Outcome, Relationship } from 'types';
import OutcomeContent from './OutcomeContent';
import KoalaButton from 'koala/components/Button';
import KoalaIcon from 'koala/components/Icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;
const PlanContainer = styled.div`
  width: 450px;
  border: 1px solid ${theme.colors.blockBorder};
  border-radius: 4px;
  background-color: ${theme.colors.N0};
  display: flex;
  flex-direction: column;

  &.focus-outcome {
    border: 1px solid ${theme.colors.B50};
  }
`;

const CustomHandle = styled(Handle)`
  background: transparent;
  border: none;
`;

const NodeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.x1};
`;

function OutcomeNode(node: NodeProps) {
  const { data } = node;
  const { t } = useTranslation();
  const { addDependentOutcomes, toggleExpand, saveContentExpanded, saveOutcomeNodeHeight, meta } = data;
  const { outcome, expanded, contentExpanded, dependentOutcomes, hideExpandable, isFocus } = meta;
  const [contentLoaded, setContentLoaded] = useState(false);
  const [isContentExpanded, setIsContentExpanded] = useState(contentExpanded);
  const containerRef = useRef<HTMLDivElement>(null);
  const [relationshipsLoaded, setRelationshipsLoaded] = useState(false);
  const history = useHistory();

  // Get the outcomes
  const queryKey = [queryKeys.relationships, outcome.id, 'outcomes'];
  useQuery(queryKey, remoteApi.fetchRelationshipsForOutcome, {
    onSuccess: (response: any) => {
      const outcomeRelationships: Relationship[] = response.data;
      const dependents: Outcome[] = outcomeRelationships
        .filter(
          (r) => r.target_id === outcome.id && r.relationship_type === 'contributes_to' && r.source_type === 'Outcome',
        )
        .map((r) => r.source as Outcome);
      addDependentOutcomes(node.id, dependents);
      setRelationshipsLoaded(true);
    },
  });

  // when the initiatives are expanded, saves the height to the node to modify layout
  useEffect(() => {
    if (contentLoaded) {
      const height = containerRef.current?.offsetHeight;
      if (height) {
        saveContentExpanded(node.id, height, isContentExpanded);
      }
    }
  }, [contentExpanded, node.id, contentLoaded, saveContentExpanded, isContentExpanded]);

  // on initial load, saves the default height to the node
  useEffect(() => {
    if (relationshipsLoaded) {
      const height = containerRef.current?.offsetHeight;
      if (height) {
        saveOutcomeNodeHeight(node.id, height, isContentExpanded);
      }
    }
  }, [isContentExpanded, node.id, relationshipsLoaded, saveContentExpanded, saveOutcomeNodeHeight]);

  // used to show children
  const toggleNodeExpanded = () => {
    toggleExpand(node.id, !expanded);
  };

  const handleOutcomeClicked = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    history.push(`#outcome:${outcome.nano_slug}:show`);
  };

  const hasChildren = dependentOutcomes && dependentOutcomes.length > 0;
  const expandedDataClassName = contentExpanded ? 'show-data' : '';
  const focusClassName = isFocus ? 'focus-outcome' : '';
  return (
    <Container onClick={(e) => e.stopPropagation()} className={expandedDataClassName} ref={containerRef}>
      <CustomHandle position={Position.Top} type="target" id="in" key="in" isConnectableStart={false} />
      <NodeContent>
        <PlanContainer className={`${expandedDataClassName} ${focusClassName}`}>
          <OutcomeContent
            outcome={outcome}
            handleOutcomeClicked={handleOutcomeClicked}
            setContentLoaded={setContentLoaded}
            isContentExpanded={isContentExpanded}
            setIsContentExpanded={setIsContentExpanded}
          />
        </PlanContainer>
        {!hideExpandable && (
          <>
            {hasChildren && !expanded && (
              <div>
                <KoalaButton onClick={toggleNodeExpanded} appearance="secondary">
                  <KoalaIcon iconName="downChevron" style={{ transform: 'rotate(-90deg)' }} />
                  {t('workspacePlan.map.expand')}
                </KoalaButton>
              </div>
            )}
            {hasChildren && expanded && (
              <div>
                <KoalaButton onClick={toggleNodeExpanded} appearance="secondary">
                  <KoalaIcon iconName="minus" />
                  {t('workspacePlan.map.collapse')}
                </KoalaButton>
              </div>
            )}
          </>
        )}
      </NodeContent>
      <CustomHandle type="source" position={Position.Bottom} key="out" id="out" isConnectableStart={false} />
    </Container>
  );
}

export default React.memo(OutcomeNode);
